import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import Pagination from '../../common/Pagination';
import SocietySectionToolbar from './SocietySectionToolbar';
import SectionFilterSelect from './SectionFilterSelect';
import { firstImageOrPlaceholder, IMAGE_PLACEHOLDER_PATH } from '../../../utils/imageFallback';

const SocietyPlaces = ({ society, onError }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    sort: 'newest'
  });

  // Load places
  useEffect(() => {
    const loadPlaces = async () => {
      setLoading(true);
      try {
        // TODO: Implement actual API call to load places
        // For now, set empty data to prevent errors
        setPlaces([]);
        setTotalPages(1);
        setTotalCount(0);
      } catch (error) {
        console.error('Error loading places:', error);
        onError('Failed to load places');
      } finally {
        setLoading(false);
      }
    };

    if (society?.id) {
      loadPlaces();
    }
  }, [society?.id, currentPage, filters, onError]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <svg
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'
        }`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ));
  };

  const getCategoryColor = (category) => {
    const colors = {
      park: 'bg-green-100 text-green-800',
      community: 'bg-blue-100 text-blue-800',
      sports: 'bg-orange-100 text-orange-800',
      recreation: 'bg-purple-100 text-purple-800',
      shopping: 'bg-pink-100 text-pink-800',
      dining: 'bg-yellow-100 text-yellow-800'
    };
    return colors[category] || colors.community;
  };

  // Safety check
  if (!society) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center text-gray-500">Loading society information...</div>
      </div>
    );
  }

  const activeFilterCount =
    (filters.category !== 'all' ? 1 : 0) + (filters.sort !== 'newest' ? 1 : 0);

  return (
    <div className="space-y-4 sm:space-y-6">
      <SocietySectionToolbar
        title="Places"
        count={totalCount}
        itemLabel={{ singular: 'place', plural: 'places' }}
        societyName={society.name}
        searchPlaceholder="Search places..."
        searchValue={filters.search}
        onSearchChange={(v) => handleFilterChange({ ...filters, search: v })}
        activeFilterCount={activeFilterCount}
        addButton={
          isAuthenticated
            ? {
                label: 'Add Place',
                onClick: () =>
                  navigate(`/societies/${society.slug}/places/create?society=${society.id}`),
              }
            : null
        }
      >
        <SectionFilterSelect
          label="Category"
          value={filters.category}
          onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
        >
          <option value="all">All Categories</option>
          <option value="park">Park</option>
          <option value="community">Community</option>
          <option value="sports">Sports</option>
          <option value="recreation">Recreation</option>
          <option value="shopping">Shopping</option>
          <option value="dining">Dining</option>
        </SectionFilterSelect>
        <SectionFilterSelect
          label="Sort By"
          value={filters.sort}
          onChange={(e) => handleFilterChange({ ...filters, sort: e.target.value })}
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="highest_rated">Highest Rated</option>
          <option value="name">Name A-Z</option>
        </SectionFilterSelect>
      </SocietySectionToolbar>

      {/* Places Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-6">
        {loading ? (
          <div className="col-span-full flex justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        ) : places.length > 0 ? (
          places.map((place) => (
            <div 
              key={place.id} 
              className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow duration-200 cursor-pointer"
              onClick={() => navigate(`/clubs/${place.id}`)}
            >
              {/* Place Image */}
              <div className="h-48 bg-gray-200 flex items-center justify-center relative">
                <img
                  src={firstImageOrPlaceholder(place.images)}
                  alt={place.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = IMAGE_PLACEHOLDER_PATH;
                  }}
                />
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(place.category)}`}>
                    {place.category.charAt(0).toUpperCase() + place.category.slice(1)}
                  </span>
                </div>
                {!place.is_public && (
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Members Only
                    </span>
                  </div>
                )}
              </div>

              {/* Place Info */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                  {place.name}
                </h3>
                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                  {place.description}
                </p>

                <div className="flex items-center mb-3">
                  <div className="flex items-center">
                    {renderStars(place.rating)}
                  </div>
                  <span className="ml-2 text-sm text-gray-600">
                    {place.rating.toFixed(1)} ({place.total_ratings} reviews)
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="line-clamp-1">{place.address}</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{place.hours}</span>
                  </div>
                  {place.phone && (
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{place.phone}</span>
                    </div>
                  )}
                </div>

                {/* Facilities */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Facilities</h4>
                  <div className="flex flex-wrap gap-1">
                    {place.facilities.map((facility, index) => (
                      <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                        {facility}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-blue-700 transition-colors duration-200">
                    View Details
                  </button>
                  <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors duration-200">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full bg-white rounded-lg shadow-sm border p-8 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No places yet</h3>
            <p className="text-gray-500 mb-4">Be the first to add a place to this society.</p>
            {isAuthenticated && (
              <button className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors duration-200">
                Add First Place
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default SocietyPlaces;
